import React, { useState, useEffect } from 'react';
import { Calendar, Plus, ExternalLink, Copy, Check } from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { supabase } from '../lib/supabase';
import { useAppStore } from '../store';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface Workshop {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

const WorkshopCalendar: React.FC = () => {
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const { toast, ToastComponent } = useToast();
  const navigate = useNavigate();
  const { suppliers } = useAppStore();

  useEffect(() => {
    loadWorkshops();
  }, [suppliers]);

  const loadWorkshops = () => {
    const workshopSuppliers = suppliers.filter(
      s => s.category?.toLowerCase() === 'taller'
    );
    setWorkshops(workshopSuppliers);
    setLoading(false);
  };

  const handleWorkshopSelect = (workshopId: string) => {
    setSelectedWorkshopId(workshopId);
    if (workshopId) {
      navigate(`/calendario-talleres/${workshopId}`);
    }
  };

  const handleCopyPublicLink = async (workshopId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_or_create_workshop_link', {
        p_workshop_id: workshopId
      });

      if (error) throw error;

      const publicUrl = `${window.location.origin}/calendario-publico/${data}`;
      await navigator.clipboard.writeText(publicUrl);

      setCopiedToken(workshopId);
      toast.success('Link copiado', 'El link público ha sido copiado al portapapeles');

      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error: any) {
      console.error('Error copying public link:', error);
      toast.error('Error', 'No se pudo copiar el link público');
    }
  };

  const handleViewCalendar = (workshopId: string) => {
    navigate(`/calendario-talleres/${workshopId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando talleres...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {ToastComponent}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Calendar className="h-8 w-8 mr-3 text-blue-600" />
            Calendario de Talleres
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Planifica y gestiona los envíos a talleres
          </p>
        </div>
      </div>

      <Card>
        <CardHeader title="Seleccionar Taller" icon={<Calendar className="h-5 w-5" />} />
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Selecciona un taller para ver su calendario de producciones
            </p>

            <Select
              label="Taller"
              value={selectedWorkshopId}
              onChange={(e) => handleWorkshopSelect(e.target.value)}
              options={[
                { value: '', label: 'Seleccionar taller...' },
                ...workshops.map(w => ({ value: w.id, label: w.name }))
              ]}
            />

            {workshops.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No hay talleres disponibles. Asegúrate de tener proveedores con categoría "Taller".
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {workshops.length > 0 && (
        <Card>
          <CardHeader title="Talleres Disponibles" icon={<Calendar className="h-5 w-5" />} />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workshops.map(workshop => (
                <div
                  key={workshop.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {workshop.name}
                  </h3>

                  {workshop.email && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      {workshop.email}
                    </p>
                  )}

                  {workshop.phone && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                      {workshop.phone}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleViewCalendar(workshop.id)}
                      icon={<Calendar className="h-4 w-4" />}
                      className="flex-1"
                    >
                      Ver Calendario
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyPublicLink(workshop.id)}
                      icon={copiedToken === workshop.id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      title="Copiar link público"
                    >
                      {copiedToken === workshop.id ? 'Copiado' : 'Link'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkshopCalendar;
